import React from "react";

/**
 * Unified LoadingState component - consolidates LoadingSpinner, Skeleton, and SkeletonLoader
 *
 * @param {Object} props
 * @param {string} props.type - Type of loading state (spinner, skeleton, dots, pulse, skeleton-text, skeleton-card, skeleton-table, skeleton-list)
 * @param {string} props.size - Size (xs, sm, md, lg, xl)
 * @param {string} props.variant - Color/style variant
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.fullScreen - Full screen overlay mode
 * @param {string} props.label - Accessible label
 * @param {boolean} props.showLabel - Show label visibly
 * @param {number} props.count - Number of skeleton items
 * @param {string} props.width - Width for skeleton elements
 * @param {string} props.height - Height for skeleton elements
 * @param {number} props.lines - Lines for skeleton text
 * @param {number} props.rows - Rows for skeleton table
 * @param {number} props.cols - Columns for skeleton table
 * @param {boolean} props.withImage - Include image placeholder
 * @param {boolean} props.animate - Enable animations
 */
const LoadingState = React.memo(
  ({
    type = "spinner",
    size = "md",
    variant = "primary",
    className = "",
    fullScreen = false,
    label = "Loading...",
    showLabel = false,
    count = 1,
    width = "full",
    height,
    lines = 3,
    rows = 5,
    cols = 4,
    withImage = false,
    animate = true,
    ...props
  }) => {
    // Size classes
    const sizeClasses = {
      xs: "w-4 h-4",
      sm: "w-5 h-5",
      md: "w-8 h-8",
      lg: "w-10 h-10",
      xl: "w-12 h-12",
    };

    // Color variants
    const variantClasses = {
      primary: "text-primary-500 dark:text-primary-400",
      secondary: "text-gray-600 dark:text-gray-400",
      success: "text-green-500 dark:text-green-400",
      warning: "text-yellow-500 dark:text-yellow-400",
      danger: "text-red-500 dark:text-red-400",
      info: "text-blue-500 dark:text-blue-400",
      white: "text-white",
      dark: "text-gray-800 dark:text-gray-200",
      inherit: "text-current",
    };

    // Skeleton base classes
    const skeletonBaseClasses = `
    bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 
    dark:from-gray-700 dark:via-gray-600 dark:to-gray-700
    ${animate ? "animate-pulse" : ""}
  `;

    // Width calculation for skeleton
    const getWidthClass = () => {
      if (
        typeof width === "string" &&
        !width.includes("px") &&
        !width.includes("%")
      ) {
        return width === "full"
          ? "w-full"
          : width === "auto"
            ? "w-auto"
            : width;
      }
      return "";
    };

    // Height calculation for skeleton variants
    const getSkeletonHeight = () => {
      if (height)
        return height.includes("px") || height.includes("%")
          ? ""
          : `h-${height}`;

      switch (type) {
        case "skeleton-text":
          return "h-4";
        case "skeleton-title":
          return "h-6";
        case "skeleton-avatar":
          return "h-10 w-10";
        case "skeleton-avatar-lg":
          return "h-16 w-16";
        case "skeleton-card":
          return "h-32";
        case "skeleton-button":
          return "h-10";
        case "skeleton-input":
          return "h-10";
        case "skeleton-icon":
          return "h-6 w-6";
        default:
          return "";
      }
    };

    // Rounded corners for skeleton
    const getSkeletonRounded = () => {
      switch (type) {
        case "skeleton-avatar":
        case "skeleton-avatar-lg":
        case "skeleton-icon":
          return "rounded-full";
        case "skeleton-button":
          return "rounded-md";
        default:
          return "rounded";
      }
    };

    // Render spinner types
    const renderSpinner = () => {
      const spinnerClass = `${sizeClasses[size]} ${variantClasses[variant]} ${className}`;

      switch (type) {
        case "spinner":
          return (
            <svg
              className={`animate-spin ${spinnerClass}`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
              {...props}
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          );

        case "dots":
          const dotSize = {
            xs: "w-1 h-1",
            sm: "w-1.5 h-1.5",
            md: "w-2 h-2",
            lg: "w-2.5 h-2.5",
            xl: "w-3 h-3",
          };
          return (
            <div
              className={`flex items-center space-x-1 ${variantClasses[variant]} ${className}`}
              aria-hidden="true"
              {...props}
            >
              {[0, 1, 2].map((dot) => (
                <div
                  key={dot}
                  className={`${dotSize[size]} rounded-full bg-current animate-bounce`}
                  style={{ animationDelay: `${dot * 150}ms` }}
                />
              ))}
            </div>
          );

        case "pulse":
          return (
            <div
              className={`relative ${sizeClasses[size]} ${className}`}
              aria-hidden="true"
              {...props}
            >
              <div
                className={`absolute inset-0 rounded-full bg-current ${variantClasses[variant]} opacity-60 animate-ping`}
              />
              <div
                className={`absolute inset-0 rounded-full bg-current ${variantClasses[variant]} opacity-40 animate-ping`}
                style={{ animationDelay: "0.5s" }}
              />
              <div
                className={`relative rounded-full bg-current ${variantClasses[variant]} h-full w-full`}
              />
            </div>
          );

        default:
          return null;
      }
    };

    // Render skeleton elements
    const renderSkeleton = () => {
      const skeletonClass = `
      ${skeletonBaseClasses}
      ${getWidthClass()}
      ${getSkeletonHeight()}
      ${getSkeletonRounded()}
      ${className}
    `;

      const getInlineStyles = () => {
        const styles = {};
        if (
          typeof width === "string" &&
          (width.includes("px") || width.includes("%"))
        ) {
          styles.width = width;
        }
        if (
          typeof height === "string" &&
          (height.includes("px") || height.includes("%"))
        ) {
          styles.height = height;
        }
        return styles;
      };

      switch (type) {
        case "skeleton":
        case "skeleton-text":
        case "skeleton-title":
        case "skeleton-avatar":
        case "skeleton-avatar-lg":
        case "skeleton-button":
        case "skeleton-input":
        case "skeleton-icon":
          if (count <= 1) {
            return (
              <div
                className={skeletonClass}
                style={getInlineStyles()}
                {...props}
              />
            );
          }
          return [...Array(count)].map((_, index) => (
            <div
              key={index}
              className={`${skeletonClass} ${type === "skeleton-text" ? "mb-2" : "mb-3"}`}
              style={getInlineStyles()}
            />
          ));

        case "skeleton-multiline":
          return (
            <div className={`space-y-2 ${className}`} {...props}>
              {[...Array(lines)].map((_, index) => (
                <div
                  key={index}
                  className={`${skeletonBaseClasses} h-4 rounded ${index === lines - 1 && lines > 1 ? "w-3/4" : "w-full"}`}
                />
              ))}
            </div>
          );

        case "skeleton-card":
          return (
            <div
              className={`border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden ${className}`}
              {...props}
            >
              {withImage && <div className={`${skeletonBaseClasses} h-32`} />}
              <div className="p-4 space-y-3">
                <div className={`${skeletonBaseClasses} h-6 w-3/5 rounded`} />
                <div className="space-y-2">
                  {[...Array(lines)].map((_, index) => (
                    <div
                      key={index}
                      className={`${skeletonBaseClasses} h-4 rounded ${index === lines - 1 ? "w-3/4" : "w-full"}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          );

        case "skeleton-table":
          return (
            <div
              className={`border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden ${className}`}
              {...props}
            >
              {/* Header */}
              <div
                className="bg-gray-50 dark:bg-gray-800 p-4 border-b border-gray-200 dark:border-gray-700 grid gap-4"
                style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
              >
                {[...Array(cols)].map((_, index) => (
                  <div
                    key={index}
                    className={`${skeletonBaseClasses} h-4 w-4/5 rounded`}
                  />
                ))}
              </div>
              {/* Rows */}
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {[...Array(rows)].map((_, rowIndex) => (
                  <div
                    key={rowIndex}
                    className="p-4 grid gap-4"
                    style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
                  >
                    {[...Array(cols)].map((_, colIndex) => (
                      <div
                        key={`${rowIndex}-${colIndex}`}
                        className={`${skeletonBaseClasses} h-4 rounded ${colIndex === 0 ? "w-3/5" : "w-4/5"}`}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          );

        case "skeleton-list":
          return (
            <div
              className={`divide-y divide-gray-200 dark:divide-gray-700 ${className}`}
              {...props}
            >
              {[...Array(count)].map((_, index) => (
                <div key={index} className="py-3 flex items-center space-x-3">
                  {withImage && (
                    <div
                      className={`${skeletonBaseClasses} h-10 w-10 rounded-full`}
                    />
                  )}
                  <div className="space-y-1 flex-1">
                    <div
                      className={`${skeletonBaseClasses} h-4 w-2/5 rounded`}
                    />
                    <div
                      className={`${skeletonBaseClasses} h-4 w-4/5 rounded`}
                    />
                  </div>
                </div>
              ))}
            </div>
          );

        default:
          return null;
      }
    };

    // Render based on type
    const renderContent = () => {
      if (type.startsWith("skeleton")) {
        return renderSkeleton();
      }
      return renderSpinner();
    };

    // Full screen wrapper
    if (fullScreen) {
      return (
        <div className="fixed inset-0 flex items-center justify-center bg-surface/90 z-50">
          <div className="text-center bg-surface-container p-8 rounded-xl shadow-2xl">
            {renderContent()}
            {label &&
              (showLabel ? (
                <p className="mt-4 text-on-surface font-medium">{label}</p>
              ) : (
                <span className="sr-only">{label}</span>
              ))}
          </div>
        </div>
      );
    }

    // Regular wrapper
    return (
      <div className="inline-flex flex-col items-center justify-center">
        {renderContent()}
        {label &&
          !type.startsWith("skeleton") &&
          (showLabel ? (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {label}
            </p>
          ) : (
            <span className="sr-only">{label}</span>
          ))}
      </div>
    );
  },
);

LoadingState.displayName = "LoadingState";

export default LoadingState;

// Convenience exports for backward compatibility and specific use cases
export const LoadingSpinner = (props) => (
  <LoadingState type="spinner" {...props} />
);
export const Skeleton = (props) => <LoadingState type="skeleton" {...props} />;
export const SkeletonText = (props) => (
  <LoadingState type="skeleton-multiline" {...props} />
);
export const SkeletonCard = (props) => (
  <LoadingState type="skeleton-card" {...props} />
);
export const SkeletonTable = (props) => (
  <LoadingState type="skeleton-table" {...props} />
);
export const SkeletonList = (props) => (
  <LoadingState type="skeleton-list" {...props} />
);
